// Intent-time preload seam for chat-side modules. Fire-and-forget; experiment-gated; once per page load.
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { preloadLazyCardWithUrlContent } from '@atlaskit/smart-card/preload-lazy-card-with-url-content';
import { importAutoSuggest } from '@atlassian/conversation-assistant-auto-suggest/import-auto-suggest';

import { importFeedbackFlag } from './ui/feedback-collector/feedback-flag/import-feedback-flag';
import { importSmartCreationWrapper } from './ui/smart-creation-wrapper/import-smart-creation-wrapper';

const PRELOADER_SMART_CARD = 'smart-card';
const PRELOADER_AUTO_SUGGEST = 'auto-suggest';
const PRELOADER_SMART_CREATION = 'smart-creation';
const PRELOADER_FEEDBACK_FLAG = 'feedback-flag';

const ALL_PRELOADERS = [
	PRELOADER_SMART_CARD,
	PRELOADER_AUTO_SUGGEST,
	PRELOADER_SMART_CREATION,
	PRELOADER_FEEDBACK_FLAG,
] as const;

type PreloaderName = (typeof ALL_PRELOADERS)[number];

let hasPreloadedChatOnIntent = false;

function isPreloaderName(preloader: string): preloader is PreloaderName {
	return (ALL_PRELOADERS as readonly string[]).includes(preloader);
}

function getEnabledPreloaders(cohort: string): Set<PreloaderName> {
	return new Set(
		cohort
			.split(',')
			.map((preloader) => preloader.trim())
			.filter(Boolean)
			.filter(isPreloaderName),
	);
}

function preloadSmartCard(): void {
	void preloadLazyCardWithUrlContent().catch(() => {});
}

function preloadAutoSuggest(): void {
	void importAutoSuggest().catch(() => {});
}

function preloadSmartCreationWrapper(): void {
	void importSmartCreationWrapper().catch(() => {});
}

function preloadFeedbackFlag(): void {
	void importFeedbackFlag().catch(() => {});
}

export function preloadChatOnIntent(): void {
	if (typeof window === 'undefined') {
		return;
	}

	if (hasPreloadedChatOnIntent) {
		return;
	}

	hasPreloadedChatOnIntent = true;

	const enabledPreloaders = getEnabledPreloaders(
		FeatureGates.getExperimentValue('chat_preload_components_exp', 'cohort', '', {
			fireExperimentExposure: true,
		}),
	);

	if (enabledPreloaders.size === 0) {
		return;
	}

	if (enabledPreloaders.has(PRELOADER_SMART_CARD)) {
		preloadSmartCard();
	}

	if (enabledPreloaders.has(PRELOADER_AUTO_SUGGEST)) {
		preloadAutoSuggest();
	}

	if (enabledPreloaders.has(PRELOADER_SMART_CREATION)) {
		preloadSmartCreationWrapper();
	}

	if (enabledPreloaders.has(PRELOADER_FEEDBACK_FLAG)) {
		preloadFeedbackFlag();
	}
}
